// import type { DashboardMeta } from '@/lib/dashboardTypes'; // Use if needed for future-proofing
import { useCallback, useEffect, useState } from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { isAdminAuthenticated } from "@/lib/adminAuth";
import { isBetaAdminAuthenticated } from "@/lib/betaAuth";

import {
  subscribeDashboardMeta,
  loadDashboardMeta,
  saveDashboardMeta,
} from "@/lib/dashboardStore";
import updateSharedDashboardMeta from "@/lib/dashboardServerApi";
// All dashboardStore functions are now statically imported

export function ComingSoonToggleCard() {
  const [comingSoonEnabled, setComingSoonEnabled] = useState(false);
  const [metaReady, setMetaReady] = useState(false);

  const canEdit = isAdminAuthenticated() || isBetaAdminAuthenticated();

  useEffect(() => {
    let isActive = true;
    let unsubscribe: (() => void) | undefined;

    const applyMeta = (meta: { comingSoonEnabled?: boolean } | null) => {
      if (!isActive) return;
      setComingSoonEnabled(Boolean(meta?.comingSoonEnabled));
    };

    try {
      unsubscribe = subscribeDashboardMeta((meta) => {
        applyMeta(meta);
        if (isActive) setMetaReady(true);
      });
    } catch {
      // noop: fall through to load
    }

    if (!unsubscribe) {
      (async () => {
        try {
          const meta = await loadDashboardMeta();
          applyMeta(meta);
        } catch {
          // ignore
        } finally {
          if (isActive) setMetaReady(true);
        }
      })();
    }

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, []);

  const handleToggle = useCallback(
    (nextEnabled: boolean) => {
      setComingSoonEnabled(nextEnabled);
      if (!canEdit) return;
      (async () => {
        const ok = await updateSharedDashboardMeta({
          comingSoonEnabled: nextEnabled,
        });
        if (!ok) {
          // fallback to direct write if server update not available
          void saveDashboardMeta({ comingSoonEnabled: nextEnabled });
        }
      })();
    },
    [canEdit],
  );

  return (
    <Card className="border-border/60 bg-card/60 backdrop-blur">
      <CardHeader className="space-y-1">
        <CardTitle className="text-base">Coming Soon</CardTitle>
        <CardDescription>
          Show or hide the "Coming Soon" message on the landing page.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="developer-coming-soon" className="text-sm">
            {comingSoonEnabled ? "Coming Soon on" : "Coming Soon off"}
          </Label>
          <Switch
            id="developer-coming-soon"
            checked={comingSoonEnabled}
            disabled={!metaReady || !canEdit}
            onCheckedChange={handleToggle}
          />
        </div>
        {!metaReady && (
          <p className="text-xs text-muted-foreground">
            Checking dashboard settings…
          </p>
        )}
        {!canEdit && (
          <p className="text-xs text-muted-foreground">
            Admin access required to change this setting.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function BetaEnableCard() {
  const [betaEnabled, setBetaEnabled] = useState(false);
  const [metaReady, setMetaReady] = useState(false);

  const canEdit = isAdminAuthenticated() || isBetaAdminAuthenticated();

  useEffect(() => {
    let isActive = true;
    let unsubscribe: (() => void) | undefined;

    const applyMeta = (meta: { betaEnabled?: boolean } | null) => {
      if (!isActive) return;
      setBetaEnabled(Boolean(meta?.betaEnabled));
    };

    try {
      unsubscribe = subscribeDashboardMeta((meta) => {
        applyMeta(meta);
        if (isActive) setMetaReady(true);
      });
    } catch {
      // noop: fall through to load
    }

    if (!unsubscribe) {
      (async () => {
        try {
          const meta = await loadDashboardMeta();
          applyMeta(meta);
        } catch {
          // ignore
        } finally {
          if (isActive) setMetaReady(true);
        }
      })();
    }

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, []);

  const handleToggle = useCallback(
    (nextEnabled: boolean) => {
      setBetaEnabled(nextEnabled);
      if (!canEdit) return;
      (async () => {
        const ok = await updateSharedDashboardMeta({
          betaEnabled: nextEnabled,
        });
        if (!ok) {
          void saveDashboardMeta({ betaEnabled: nextEnabled });
        }
      })();
    },
    [canEdit],
  );

  return (
    <Card className="border-border/60 bg-card/60 backdrop-blur">
      <CardHeader className="space-y-1">
        <CardTitle className="text-base">Beta access</CardTitle>
        <CardDescription>
          Enable or disable the beta program. When disabled, the beta path is
          inaccessible.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="beta-enable" className="text-sm">
            {betaEnabled ? "Beta enabled" : "Beta disabled"}
          </Label>
          <Switch
            id="beta-enable"
            checked={betaEnabled}
            disabled={!metaReady || !canEdit}
            onCheckedChange={handleToggle}
          />
        </div>
        {!metaReady && (
          <p className="text-xs text-muted-foreground">
            Checking dashboard settings…
          </p>
        )}
        {!canEdit && (
          <p className="text-xs text-muted-foreground">
            Admin access required to change this setting.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function BetaDashboardVisibilityCard() {
  const [visibleInBeta, setVisibleInBeta] = useState(false);
  const [metaReady, setMetaReady] = useState(false);

  const canEdit = isAdminAuthenticated() || isBetaAdminAuthenticated();

  useEffect(() => {
    let isActive = true;
    let unsubscribe: (() => void) | undefined;

    const applyMeta = (meta: { betaDashboardsVisible?: boolean } | null) => {
      if (!isActive) return;
      setVisibleInBeta(Boolean(meta?.betaDashboardsVisible));
    };

    try {
      unsubscribe = subscribeDashboardMeta((meta) => {
        applyMeta(meta);
        if (isActive) setMetaReady(true);
      });
    } catch {
      // noop: fall through to load
    }

    if (!unsubscribe) {
      (async () => {
        try {
          const meta = await loadDashboardMeta();
          applyMeta(meta);
        } catch {
          // ignore
        } finally {
          if (isActive) setMetaReady(true);
        }
      })();
    }

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, []);

  const handleToggle = useCallback(
    (nextEnabled: boolean) => {
      setVisibleInBeta(nextEnabled);
      if (!canEdit) return;
      (async () => {
        const ok = await updateSharedDashboardMeta({
          betaDashboardsVisible: nextEnabled,
        });
        if (!ok) {
          void saveDashboardMeta({ betaDashboardsVisible: nextEnabled });
        }
      })();
    },
    [canEdit],
  );

  return (
    <Card className="border-border/60 bg-card/60 backdrop-blur">
      <CardHeader className="space-y-1">
        <CardTitle className="text-base">Beta dashboard visibility</CardTitle>
        <CardDescription>
          Allow signed-in beta users to see the global dashboards in their beta
          experience.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="beta-dashboard-visibility" className="text-sm">
            {visibleInBeta
              ? "Dashboards shared to beta"
              : "Dashboards hidden from beta"}
          </Label>
          <Switch
            id="beta-dashboard-visibility"
            checked={visibleInBeta}
            disabled={!metaReady || !canEdit}
            onCheckedChange={handleToggle}
          />
        </div>
        {!metaReady && (
          <p className="text-xs text-muted-foreground">
            Checking dashboard settings…
          </p>
        )}
        {!canEdit && (
          <p className="text-xs text-muted-foreground">
            Admin access required to change this setting.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
